import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { User } from "@/types";

/**
 * Auth context — exposes the current user + every auth-related action.
 *
 * `user` is the discriminated source of truth used across the SPA:
 *   • null  → still loading (first /auth/me probe in flight)
 *   • false → confirmed anonymous (probe returned 401)
 *   • User  → signed in
 *
 * iter49 TS Phase 2 — migrated to TypeScript. Public API + behaviour
 * unchanged; only annotations + `null`-safe context-default added.
 */

// Login response can be one-step (got the user back) OR two-step (got an OTP
// challenge). Discriminated by `otp_required`.
export interface LoginOtpChallenge {
  otp_required: true;
  challenge_id: string;
  delivery: "email";
  ttl_minutes: number;
  dev_rerouted_to?: string;
}

export type LoginResponse = User | LoginOtpChallenge | false;

export interface RegisterPendingVerification {
  verification_required: true;
  email: string;
}

export type RegisterResponse = User | RegisterPendingVerification | false;

export interface AuthContextValue {
  user: User | false | null;
  error: string;
  login: (email: string, password: string, turnstileToken: string | null) => Promise<LoginResponse>;
  verifyLoginOtp: (challenge_id: string, code: string) => Promise<User | false>;
  register: (email: string, password: string, name: string, turnstileToken: string | null) => Promise<RegisterResponse>;
  resendVerification: (email: string) => Promise<boolean>;
  verifyEmailToken: (token: string) => Promise<boolean>;
  requestPasswordReset: (email: string, turnstileToken: string | null) => Promise<boolean>;
  resetPassword: (token: string, new_password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refetch: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // null = loading, false = anon, User = signed in
  const [user, setUser] = useState<User | false | null>(null);
  const [error, setError] = useState<string>("");

  // iter49 P3 #17: fetchMe MUST stay wrapped in useCallback with empty deps
  // so it's referentially stable. The useEffect below depends on it; if a
  // future refactor unwraps the useCallback, every render creates a new
  // fetchMe → useEffect re-runs → infinite loop. The empty-deps closure is
  // safe because fetchMe only ever calls api.get + setUser (both stable).
  const fetchMe = useCallback(async (): Promise<User | null> => {
    try {
      const { data } = await api.get<User>("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login: AuthContextValue["login"] = async (email, password, turnstileToken) => {
    setError("");
    try {
      const { data } = await api.post<User | LoginOtpChallenge>("/auth/login", {
        email,
        password,
        turnstile_token: turnstileToken,
      });
      // 2-step login: if OTP is required, return the challenge to the page
      // which then renders the OTP step. The session token is only minted
      // after /auth/login/verify-otp succeeds.
      if ((data as LoginOtpChallenge)?.otp_required) {
        // Pass the entire response through so additive fields like
        // `dev_rerouted_to` (only set in DEV_MODE) reach the OTP screen.
        return data as LoginOtpChallenge;
      }
      setUser(data as User);
      return data as User;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const verifyLoginOtp: AuthContextValue["verifyLoginOtp"] = async (challenge_id, code) => {
    setError("");
    try {
      const { data } = await api.post<User>("/auth/login/verify-otp", { challenge_id, code });
      setUser(data);
      return data;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const register: AuthContextValue["register"] = async (email, password, name, turnstileToken) => {
    setError("");
    try {
      const { data } = await api.post<User | RegisterPendingVerification>("/auth/register", {
        email,
        password,
        name,
        turnstile_token: turnstileToken,
      });
      // New flow: signup returns {verification_required: true} instead of a
      // session token. The page shows a "check your inbox" screen until the
      // user clicks the link.
      const verif = data as RegisterPendingVerification;
      if (verif?.verification_required) {
        return { verification_required: true, email: verif.email };
      }
      setUser(data as User);
      return data as User;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const requestPasswordReset: AuthContextValue["requestPasswordReset"] = async (email, turnstileToken) => {
    setError("");
    try {
      await api.post("/auth/forgot-password", { email, turnstile_token: turnstileToken });
      return true; // always reports success — endpoint is anti-enumeration by design
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const resetPassword: AuthContextValue["resetPassword"] = async (token, new_password) => {
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password });
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const verifyEmailToken: AuthContextValue["verifyEmailToken"] = async (token) => {
    setError("");
    try {
      await api.post(`/auth/verify-email?token=${encodeURIComponent(token)}`);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const resendVerification: AuthContextValue["resendVerification"] = async (email) => {
    setError("");
    try {
      await api.post("/auth/resend-verification", { email });
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const logout: AuthContextValue["logout"] = async () => {
    try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
    setUser(false);
  };

  const value: AuthContextValue = {
    user,
    error,
    login,
    verifyLoginOtp,
    register,
    resendVerification,
    verifyEmailToken,
    requestPasswordReset,
    resetPassword,
    logout,
    refetch: fetchMe,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * useAuth — primary hook every authed page consumes.
 *
 * Returns a NON-nullable AuthContextValue. If a consumer ever lands outside
 * the provider tree (a test, a Storybook story), the cast still gives them
 * sensible undefined fields rather than runtime null-deref — but that's a
 * code bug we want to surface early, so we throw in development.
 */
export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    if (process.env.NODE_ENV !== "production") {
      throw new Error("useAuth() called outside <AuthProvider>");
    }
    // Production fallback: shape-compatible no-op so a stray consumer
    // doesn't crash the whole app.
    return {
      user: false,
      error: "",
      login: async () => false,
      verifyLoginOtp: async () => false,
      register: async () => false,
      resendVerification: async () => false,
      verifyEmailToken: async () => false,
      requestPasswordReset: async () => false,
      resetPassword: async () => false,
      logout: async () => undefined,
      refetch: async () => null,
    };
  }
  return ctx;
};
